import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiShoppingCart, FiTrash2, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const Cart = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { cartItems, cartCount, cartSubtotalCents, removeFromCart, clearCart } = useCart();

  const [isClearing, setIsClearing] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  const subtotalLabel = useMemo(() => {
    return `$${(Number(cartSubtotalCents || 0) / 100).toFixed(2)}`;
  }, [cartSubtotalCents]);

  const handleClearCart = () => {
    if (!cartItems.length) return;

    const ok = window.confirm("Remove all items from your cart?");
    if (!ok) return;

    setIsClearing(true);
    clearCart();

    window.setTimeout(() => {
      setIsClearing(false);
    }, 250);
  };

  const handleCheckout = () => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (!cartItems.length) return;

    navigate("/checkout");
  };

  const renderMeta = (item) => {
    const pieces = [];

    if (item.page_tier || item.pageTier) {
      pieces.push(`Up to ${Number(item.page_tier || item.pageTier).toLocaleString()} pages`);
    }

    if (item.qty) {
      pieces.push(`${item.qty} ${Number(item.qty) === 1 ? "credit" : "credits"}`);
    }

    if (item.minutes) {
      pieces.push(`${item.minutes} minutes`);
    }

    if (item.kind) {
      pieces.push(String(item.kind).replace(/_/g, " "));
    }

    return pieces.filter(Boolean).join(" • ");
  };

  const formatItemPrice = (item) => {
    return `$${(Number(item.price_cents || item.priceCents || 0) / 100).toFixed(2)}`;
  };

  return (
    <div style={styles.pageShell}>
      <div style={styles.headerRow}>
        <button type="button" style={styles.backButton} onClick={() => navigate("/billing")}>
          <FiArrowLeft />
          <span>Back to Billing</span>
        </button>

        <div style={styles.cartBadge} aria-label={`${cartCount} cart items`}>
          <FiShoppingCart />
          <span>{cartCount}</span>
        </div>
      </div>

      {!currentUser ? (
        <section style={styles.emptyCard}>
          <div style={styles.emptyIcon}>
            <FiShoppingCart />
          </div>
          <h2 style={styles.emptyTitle}>Please sign in to continue</h2>
          <p style={styles.emptyText}>
            You need to be signed in before purchasing plans or add-ons.
          </p>
          <button type="button" style={styles.primaryButton} onClick={() => navigate("/login")}>
            Sign in
          </button>
        </section>
      ) : cartItems.length === 0 ? (
        <section style={styles.emptyCard}>
          <div style={styles.emptyIcon}>
            <FiShoppingCart />
          </div>
          <h2 style={styles.emptyTitle}>Your cart is empty</h2>
          <p style={styles.emptyText}>
            Add Case Analyzer, Contract Insight, Doc Writer, Smart Alert, or Vision Assist add-ons
            from the Billing page.
          </p>
          <button type="button" style={styles.primaryButton} onClick={() => navigate("/billing")}>
            Browse add-ons
          </button>
        </section>
      ) : (
        <div style={{ ...styles.cartGrid, ...(isSmallScreen ? styles.cartGridMobile : null) }}>
          <main style={{ ...styles.itemsPanel, ...(isSmallScreen ? styles.itemsPanelMobile : null) }}>
            <div style={{ ...styles.panelHeader, ...(isSmallScreen ? styles.panelHeaderMobile : null) }}>
              <div>
                <div style={styles.eyebrow}>Selected add-ons</div>
                <h2 style={styles.sectionTitle}>
                  {cartCount} {cartCount === 1 ? "item" : "items"} in cart
                </h2>
              </div>

              <button
                type="button"
                style={styles.clearButton}
                onClick={handleClearCart}
                disabled={isClearing}
              >
                Clear cart
              </button>
            </div>

            <div style={styles.itemList}>
              {cartItems.map((item) => (
                <article key={item.id} style={{ ...styles.itemCard, ...(isSmallScreen ? styles.itemCardMobile : null) }}>
                  <div style={styles.itemIcon}>
                    <FiShoppingCart />
                  </div>

                  <div style={styles.itemBody}>
                    <div style={{ ...styles.itemTopLine, ...(isSmallScreen ? styles.itemTopLineMobile : null) }}>
                      <div>
                        <h3 style={styles.itemTitle}>{item.title || item.label}</h3>
                        {!!renderMeta(item) && <p style={styles.itemMeta}>{renderMeta(item)}</p>}
                      </div>

                      <strong style={styles.itemPrice}>{formatItemPrice(item)}</strong>
                    </div>

                    {!!item.description && <p style={styles.itemDesc}>{item.description}</p>}

                    <button
                      type="button"
                      style={styles.removeButton}
                      onClick={() => removeFromCart(item.id)}
                    >
                      <FiTrash2 />
                      <span>Remove</span>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </main>

          <aside style={{ ...styles.summaryPanel, ...(isSmallScreen ? styles.summaryPanelMobile : null) }}>
            <div style={styles.summaryTop}>
              <div style={styles.eyebrow}>Order summary</div>
              <h2 style={styles.summaryTitle}>Cart total</h2>
            </div>

            <div style={styles.summaryRows}>
              <div style={styles.summaryRow}>
                <span>Items</span>
                <strong>{cartCount}</strong>
              </div>

              <div style={styles.summaryRow}>
                <span>Subtotal</span>
                <strong>{subtotalLabel}</strong>
              </div>
            </div>

            <div style={styles.totalRow}>
              <span>Total due today</span>
              <strong>{subtotalLabel}</strong>
            </div>

            <p style={styles.summaryNote}>
              Promo codes and final payment details can be reviewed on the checkout page.
            </p>

            <button type="button" style={styles.checkoutButton} onClick={handleCheckout}>
              <span>Continue to checkout</span>
              <FiArrowRight />
            </button>

            <button type="button" style={styles.secondaryButton} onClick={() => navigate("/billing")}>
              Continue shopping
            </button>
          </aside>
        </div>
      )}
    </div>
  );
};

const styles = {
  pageShell: {
    width: "100%",
    maxWidth: "1180px",
    margin: "0 auto",
    padding: "8px 0 40px",
    boxSizing: "border-box",
  },

  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginBottom: "16px",
  },

  backButton: {
    border: "1px solid #D8DEE9",
    background: "#FFFFFF",
    color: "#17245F",
    borderRadius: "999px",
    padding: "10px 14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    fontWeight: 800,
    boxShadow: "0 8px 18px rgba(15, 23, 42, 0.06)",
  },

  cartBadge: {
    minWidth: "54px",
    height: "42px",
    borderRadius: "999px",
    background: "#17245F",
    color: "#FFFFFF",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontWeight: 900,
    boxShadow: "0 12px 24px rgba(23, 36, 95, 0.24)",
  },

  heroCard: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
    background: "linear-gradient(135deg, #17245F 0%, #1E2A78 58%, #0F766E 100%)",
    color: "#FFFFFF",
    borderRadius: "26px",
    padding: "26px",
    boxShadow: "0 18px 40px rgba(15, 23, 42, 0.16)",
    marginBottom: "22px",
  },

  heroIcon: {
    width: "58px",
    height: "58px",
    borderRadius: "20px",
    background: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "26px",
    flex: "0 0 auto",
  },

  h1: {
    margin: 0,
    fontSize: "clamp(30px, 4vw, 44px)",
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
  },

  heroText: {
    margin: "8px 0 0",
    color: "rgba(255,255,255,0.86)",
    fontSize: "16px",
    lineHeight: 1.5,
  },

  cartGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 360px",
    gap: "20px",
    alignItems: "start",
  },

  cartGridMobile: {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },

  itemsPanel: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "24px",
    padding: "20px",
    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
  },

  itemsPanelMobile: {
    padding: "16px",
    borderRadius: "22px",
  },

  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "14px",
    marginBottom: "16px",
  },

  panelHeaderMobile: {
    alignItems: "center",
    gap: "10px",
  },

  eyebrow: {
    color: "#64748B",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    marginBottom: "5px",
  },

  sectionTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: "22px",
    letterSpacing: "-0.025em",
  },

  clearButton: {
    border: "1px solid #FECACA",
    background: "#FEF2F2",
    color: "#B91C1C",
    borderRadius: "999px",
    padding: "9px 12px",
    cursor: "pointer",
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  itemList: {
    display: "grid",
    gap: "14px",
  },

  itemCard: {
    display: "flex",
    gap: "14px",
    border: "1px solid #E2E8F0",
    background: "#F8FAFC",
    borderRadius: "20px",
    padding: "16px",
  },

  itemCardMobile: {
    gap: "12px",
    padding: "14px",
  },

  itemIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "16px",
    background: "#E0F2FE",
    color: "#0369A1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
    fontSize: "20px",
  },

  itemBody: {
    flex: 1,
    minWidth: 0,
  },

  itemTopLine: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  },

  itemTopLineMobile: {
    gap: "10px",
  },

  itemTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: "16px",
    lineHeight: 1.3,
  },

  itemMeta: {
    margin: "5px 0 0",
    color: "#475569",
    fontSize: "13px",
    lineHeight: 1.4,
  },

  itemDesc: {
    margin: "10px 0 0",
    color: "#64748B",
    fontSize: "14px",
    lineHeight: 1.5,
  },

  itemPrice: {
    color: "#17245F",
    fontSize: "17px",
    whiteSpace: "nowrap",
  },

  removeButton: {
    marginTop: "12px",
    border: "none",
    background: "transparent",
    color: "#B91C1C",
    padding: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    fontWeight: 800,
  },

  summaryPanel: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "24px",
    padding: "20px",
    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
    position: "sticky",
    top: "86px",
  },

  summaryPanelMobile: {
    position: "static",
    top: "auto",
    width: "100%",
    boxSizing: "border-box",
    borderRadius: "22px",
  },

  summaryTop: {
    marginBottom: "16px",
  },

  summaryTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: "24px",
    letterSpacing: "-0.03em",
  },

  summaryRows: {
    borderTop: "1px solid #E2E8F0",
    borderBottom: "1px solid #E2E8F0",
    padding: "12px 0",
    display: "grid",
    gap: "10px",
  },

  summaryRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "#475569",
    fontSize: "14px",
  },

  totalRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "16px",
    color: "#0F172A",
    fontSize: "18px",
    fontWeight: 900,
  },

  summaryNote: {
    margin: "14px 0 18px",
    color: "#64748B",
    fontSize: "13px",
    lineHeight: 1.5,
  },

  checkoutButton: {
    width: "100%",
    border: "none",
    background: "#17245F",
    color: "#FFFFFF",
    borderRadius: "16px",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: "15px",
    boxShadow: "0 12px 24px rgba(23, 36, 95, 0.24)",
  },

  secondaryButton: {
    width: "100%",
    marginTop: "10px",
    border: "1px solid #CBD5E1",
    background: "#FFFFFF",
    color: "#17245F",
    borderRadius: "16px",
    padding: "13px 16px",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: "14px",
  },

  emptyCard: {
    background: "#FFFFFF",
    border: "1px solid #E2E8F0",
    borderRadius: "24px",
    padding: "34px 22px",
    textAlign: "center",
    boxShadow: "0 14px 32px rgba(15, 23, 42, 0.08)",
  },

  emptyIcon: {
    width: "62px",
    height: "62px",
    borderRadius: "22px",
    background: "#EEF2FF",
    color: "#17245F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    margin: "0 auto 14px",
  },

  emptyTitle: {
    margin: 0,
    color: "#0F172A",
    fontSize: "24px",
    letterSpacing: "-0.03em",
  },

  emptyText: {
    maxWidth: "560px",
    margin: "10px auto 18px",
    color: "#64748B",
    lineHeight: 1.6,
  },

  primaryButton: {
    border: "none",
    background: "#17245F",
    color: "#FFFFFF",
    borderRadius: "16px",
    padding: "13px 18px",
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 12px 24px rgba(23, 36, 95, 0.24)",
  },
};

export default Cart;
